<template>
	<Page animate>
		<template v-slot:content>
			<SafeAreaTop :show-close="false">
				<img class="paywall-toggle-image" src="@/assets/paywall/curved-bg.png" alt="" />
				<div class="container">
					<div class="paywall-toggle-title" v-html="t('title')"></div>
					<div class="tac mb-md" v-html="activeProduct.text"></div>
					<div class="paywall-toggle-price mb-lg">
						<div class="paywall-toggle-price-value">{{ activeProduct.price }}</div>
					</div>

					<LabeledToggle class="mb-lg" v-model="toggleVal" :values="toggleValues" />

					<div class="paywall-toggle-terms mb-xl">{{ activeProduct.subtitle }}</div>

					<div class="advantages">
						<div class="advantage" v-for="advantage of advantages" :key="advantage.id">
							<div class="advantage-icon">
								<svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M21.5114 3.05819L18.4532 0L8.67596 9.77725L3.16244 4.26373L0.104248 7.32192L8.67601 15.8937L11.7342 12.8355L11.7342 12.8354L21.5114 3.05819Z"
										fill="#7A89ED"
									/>
								</svg>
							</div>
							<div class="advantage-content">
								<div class="advantage-content--title">{{ advantage.title }}</div>
								<div class="advantage-content--value">{{ advantage.text }}</div>
							</div>
						</div>
					</div>

					<PaywallLinksBlock @restore="handleRestore" />
				</div>
			</SafeAreaTop>

			<Alert
				:isOpen="showRestoreSuccessAlert"
				:title="t('restore_popup.success.title')"
				:text="t('restore_popup.success.body')"
				@onClose="onCloseRestoreSuccessAlert"
			/>
			<Alert
				:isOpen="showRestoreErrorAlert"
				:title="t('restore_popup.error.title')"
				:text="t('restore_popup.error.body')"
				@onClose="onCloseRestoreErrorAlert"
			/>
		</template>

		<template v-slot:footer>
			<div class="container">
				<p-button @click.prevent="handleSubmit" label="" :loading="isLoading" cls="btn-primary btn-block mb-md">
					{{ t('buttonAccept') }}
				</p-button>
				<a href="" class="btn ttn btn-block btn-text btn-color-lightgray" @click.prevent="close">
					{{ t('buttonDecline') }}
				</a>
			</div>
		</template>
	</Page>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { inject, ref } from '@vue/runtime-core'
import { computed } from '@vue/reactivity'
import Page from '@/components/Page'
import SafeAreaTop from '@/components/SafeAreaTop'
import Alert from '@/components/Alert'
import PButton from '@/components/Button'
import LabeledToggle from '@/components/LabeledToggle'
import useConfig from '@/composition/config'
import useApi from '@/composition/api'
import PaywallLinksBlock from '@/components/PaywallLinksBlock'

const config = useConfig()
const store = useStore()
const api = useApi()
const amplitude = inject('amplitude')

const isLoading = computed(() => store.state.isLoading)
const showRestoreSuccessAlert = ref(false)
const showRestoreErrorAlert = ref(false)
const showPaywallCancelPrompt = ref(false)

const { t } = useI18n({
	useScope: 'global',
	messages: {
		en: {
			title: 'Try <span class="text-primary">Premium</span>',
			buttonAccept: 'CONTINUE',
			buttonDecline: 'Decline',
			products: {
				one: {
					price: '[price] / 12 weeks',
					subtitle: '[weekly_price] per week, a 12-week subscription fee is [price] and auto-renews each 12 weeks',
					toggleLabel: '12 weeks',
					text: 'Trusted by {count} subscribers'
				},
				two: {
					price: '[price] / year',
					subtitle: '[weekly_price] per week, an annual subscription fee is [price] and auto-renews yearly',
					toggleLabel: 'Yearly',
					text: '<span class="text-primary">Save [discount_per_year]</span> vs our 12 weeks plan - <b>[old_price_per_year]</b>'
				}
			},
			advantages: {
				0: {
					title: 'PERFORMANCE CHARTS',
					text: 'A comprehensive analysis and breakdown of your eating habbits, water intake, and activity'
				},
				1: {
					title: 'Personalized Insights',
					text: 'Tons of tailored educational content based on you nutrition and activity habits'
				},
				2: {
					title: 'WELLNESS COMPANION',
					text: 'It will walk by your side on this journey and  provide assistance, whenever you need it'
				}
			}
		}
	}
})

const advantages = computed(() => [
	{
		id: 0,
		title: t('advantages.0.title'),
		text: t('advantages.0.text')
	},
	{
		id: 1,
		title: t('advantages.1.title'),
		text: t('advantages.1.text')
	},
	{
		id: 2,
		title: t('advantages.2.title'),
		text: t('advantages.2.text')
	}
])

const products = computed(() => {
	const firstProduct = Object.assign(
		{
			id: 'simple_original_subscription_3_months',
			price: t('products.one.price'),
			subtitle: t('products.one.subtitle'),
			toggleLabel: t('products.one.toggleLabel'),
			text: t('products.one.text', { count: '463,927' })
		},
		config.get({ key: 'paywall.first_product', default_value: {} })
	)

	const secondProduct = Object.assign(
		{
			id: 'simple_original_subscription_12_months',
			price: t('products.two.price'),
			subtitle: t('products.two.subtitle'),
			toggleLabel: t('products.two.toggleLabel'),
			text: t('products.two.text'),
			old: {
				id: 'simple_original_subscription_3_months'
			}
		},
		config.get({ key: 'paywall.second_product', default_value: {} })
	)

	return store.getters['products/getData']([firstProduct, secondProduct])
})

const toggleVal = ref(0)
const activeProduct = computed(() => {
	return products.value[toggleVal.value]
})
const toggleValues = computed(() => products.value.map((v, i) => ({ value: i, label: v.toggleLabel })))

function close(Status = 'Close') {
	amplitude.logEvent('Paywall - Close', {
		Layout: config.get({ key: 'layout', default_value: 'Toggle Paywall' }),
		From: store.getters['getSource'],
		Step: 'Paywall',
		Status
	})
	api.close()
}

function handleRestore() {
	api.restore({
		closeOnSuccess: false,
		onFinish: () => {
			showRestoreSuccessAlert.value = true
		},
		onFail: () => {
			showRestoreErrorAlert.value = true
		}
	})
}

function onCloseRestoreSuccessAlert() {
	showRestoreErrorAlert.value = false
	close('Restore')
}

function onCloseRestoreErrorAlert() {
	showRestoreErrorAlert.value = false
}

const Purchase = (product) => {
	api.purchase({
		productId: product.id,
		onFinish: () => {
			amplitude.logEvent('Paywall - Done', {
				Layout: config.get({ key: 'layout', default_value: 'Toggle Paywall' }),
				From: store.getters['getSource'],
				Step: 'Paywall',
				ProductId: product.id,
				Status: 'Subscribe'
			})
			close('Subscribe')
		},
		onError: () => {
			console.log('error')
		},
		onCancel: () => {
			showPaywallCancelPrompt.value = true
		},
		closeOnSuccess: false
	})
}

function handleSubmit() {
	Purchase(activeProduct.value)
}
</script>

<style lang="scss" scoped>
.paywall-toggle-image {
	max-width: 500px;
	width: 100%;
	display: block;
	margin-left: auto;
	margin-right: auto;
	margin-bottom: 1.5rem;
}

:deep(.slider-value) {
	position: absolute;
	top: -3px;
	transform: translateX(-50%);
	display: block;
	width: 8px;
	height: 8px;
	background-color: #e2e3ee;
	border-radius: 50%;
	color: transparent;
	transition: all 250ms;
}

.paywall-toggle-logo {
	display: block;
	height: 31px;
	width: auto;
	margin-left: auto;
	margin-right: auto;
}

.paywall-toggle-title {
	text-align: center;
	font-size: 1.9rem;
	line-height: 1.5;
	font-weight: bold;
	margin-bottom: 1rem;

	span {
		display: block;
	}
}

.paywall-toggle-price {
	display: flex;
	align-items: center;
	justify-content: center;
}

.paywall-toggle-price-value {
	font-weight: 700;
	font-size: 1.7rem;
}

.paywall-toggle-terms {
	font-size: 0.86rem;
	text-align: center;
	letter-spacing: -0.7px;
}

.advantages {
	padding: 1.5rem;
	background-color: #f5f6f8;
	border-radius: 8px;
}

.advantage {
	display: flex;
	align-items: center;
	margin-bottom: 1.2rem;

	&:last-child {
		margin-bottom: 0;
	}

	&-icon {
		display: flex;
		justify-content: center;
		margin-right: 1.45rem;
	}

	&-content {
		width: 100%;

		&--title {
			font-weight: bold;
			text-transform: uppercase;
			font-size: 1.14rem;
		}

		&--value {
			opacity: 0.8;
			margin-top: 0.2rem;
		}
	}
}
</style>
